import React, { type JSX } from 'react';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import { SectionHeading } from '../SectionHeading';
import { Markdown } from '../Markdown';
import { PinterestImage } from '../PinterestImage';
import { InstagramPostEmbed } from '../InstagramPostEmbed';
import { ScrollableElementContainer } from '../ScrollableElementContainer';
import { defaultComponents } from '../Markdown/components/ReactMarkdown';
import { BodyObject, ComponentType } from '../../types/api';
import { LazyYoutube } from '../../wag-react/components/lazyload/LazyYoutube';
import { Image } from '../../wag-react/next-components/image';
import { Typography } from '../../wag-react/components/Typography';
import { BoxProps } from '../../wag-react/next-components/box';
import { Strong } from '../../wag-react/core-components/strong';
import { theme } from '../../wag-react/themes/base-theme';
export type ArticleBodyComponentProps = Omit<BoxProps, 'children' | 'value'> & {
  value: BodyObject;
  children?: JSX.Element;
  overrides?: { [k in ComponentType<React.PropsWithChildren<unknown>>]?: (props: Extract<BodyObject, {
    type: k;
  }>) => JSX.Element };
};
export enum ArticleBodyContentType {
  Header = 'header',
  ImageV1 = 'image',
  ImageV2 = 'image_v2',
  Infographic = 'infographic',
  Instagram = 'instagram',
  Paragraph = 'paragraph',
  Subheader = 'subheader',
  Tweet = 'tweet',
  Video = 'video_link',
}
export const ArticleBodyComponent = ({
  value,
  children,
  overrides = {}
}: ArticleBodyComponentProps) => {
  const componentMapFn = overrides[value.type];
  if (componentMapFn && typeof componentMapFn === 'function') {
    return componentMapFn(value);
  }
  if (value.type === 'header') {
    return children || <SectionHeading variant="h2" color={theme.colors.charcoal} textAlign="left" fontFamily="muliBold">
        {value.value}
      </SectionHeading>;
  }
  if (value.type === 'subheader') {
    return children || <ScrollableElementContainer id={value.id}>
        <SectionHeading variant="h2" fontSize={['30px']} color={theme.colors.charcoal} textAlign="left" fontFamily="muliBold">
          {value.value}
        </SectionHeading>
      </ScrollableElementContainer>;
  }
  if (value.type === 'paragraph') {
    return children || <Markdown version="react-markdown" components={{
      ...defaultComponents,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      p: ({
        node,
        ...props
      }) => <Typography color={theme.colors.charcoal} lineHeight="28px" {...props} />,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      b: ({
        node,
        ...props
      }) => <Strong color={theme.colors.charcoal} fontFamily="muliBold" {...props} />
    }}>
        {value.value}
      </Markdown>;
  }
  if (value.type === 'image' || value.type === 'image_v2') {
    return children || <Image width={1} borderRadius="8px" my="40px" src={value.image} alt={value.altText} />;
  }
  if (value.type === 'infographic') {
    return <PinterestImage url={value.image} imageProps={{
      alt: value.altText,
      width: ['344px', '600px'],
      height: ['344px', '600px']
    }} />;
  }
  if (value.type === 'tweet') {
    return <TwitterTweetEmbed tweetId={value.value} />;
  }
  if (value.type === 'instagram') {
    return <InstagramPostEmbed value={value.value} />;
  }
  if (value.type === 'video_link' && value.url) {
    return <LazyYoutube src={value.url} my={['24px', '32px']} mx="auto" />;
  }
  return null;
};